/**
 * useUsage Hook
 * 
 * Provides real-time usage data for the current organization.
 * Includes on-demand refresh capability for paid plans.
 * 
 * @module hooks/useUsage
 */

import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

/**
 * Usage data structure from Firestore
 */
export interface UsageData {
  /** Total requests this month */
  totalRequests: number;
  /** Daily breakdown: { "2025-12-03": 1234 } */
  daily: Record<string, number>;
  /** Per-key breakdown (for paid plan filtering) */
  byKey: Record<string, { 
    total: number; 
    daily: Record<string, number>; 
  }>;
  /** Per-API breakdown */
  byApi: Record<string, number>;
  /** When usage was last updated */
  lastUpdated: Date | null;
}

/**
 * Return value of useUsage hook
 */
export interface UseUsageReturn {
  /** Current usage data (null if loading or no data) */
  usage: UsageData | null;
  /** True while initial data is loading */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Function to trigger on-demand refresh */
  refresh: () => Promise<void>;
  /** Whether on-demand refresh is available (paid plans only) */
  canRefresh: boolean;
  /** True while refresh is in progress */
  refreshing: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get the current period ID (e.g., "2025-12")
 */
function getCurrentPeriodId(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Get the previous period ID
 */
function getPreviousPeriodId(): string {
  const now = new Date();
  now.setUTCMonth(now.getUTCMonth() - 1);
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Get the last N days as date keys
 */
export function getLastNDays(n: number): string[] {
  const days: string[] = [];
  const now = new Date();
  
  for (let i = n - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setUTCDate(date.getUTCDate() - i);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    days.push(`${year}-${month}-${day}`);
  }
  
  return days;
}

/**
 * Get day name from date key
 */
export function getDayName(dateKey: string): string {
  const date = new Date(dateKey + 'T00:00:00Z');
  return date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access usage data for the current organization.
 * 
 * @param periodId - Optional period ID (defaults to current month)
 * @returns Usage data and control functions
 * 
 * @example
 * ```tsx
 * function UsagePage() {
 *   const { usage, loading, refresh, canRefresh, refreshing } = useUsage();
 * 
 *   if (loading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       <p>Total: {usage?.totalRequests ?? 0}</p>
 *       {canRefresh && (
 *         <button onClick={refresh} disabled={refreshing}>
 *           Refresh
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useUsage(periodId?: string): UseUsageReturn {
  const { organization } = useAuth();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Use provided period or default to current month
  const period = periodId || getCurrentPeriodId();
  
  // Only paid plans can use on-demand refresh
  const canRefresh = organization?.planId !== 'free' && organization?.planId !== undefined;

  // ---------------------------------------------------------------------------
  // Real-time Listener
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Can't fetch without an organization
    if (!organization?.id) {
      setLoading(false);
      setUsage(null);
      return;
    }

    setLoading(true);
    setError(null);

    const usageRef = doc(db, 'organizations', organization.id, 'usage', period);

    const unsubscribe = onSnapshot(
      usageRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setUsage({
            totalRequests: data.totalRequests || 0,
            daily: data.daily || {},
            byKey: data.byKey || {},
            byApi: data.byApi || {},
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        } else {
          // No usage document yet - return empty data
          setUsage({
            totalRequests: 0,
            daily: {},
            byKey: {},
            byApi: {},
            lastUpdated: null,
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Usage fetch error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [organization?.id, period]);

  // ---------------------------------------------------------------------------
  // On-Demand Refresh
  // ---------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    if (!canRefresh || !organization?.id || refreshing) {
      return;
    }

    setRefreshing(true);
    setError(null);

    try {
      const refreshUsageFn = httpsCallable<
        { orgId: string },
        { success: boolean; totalMonth: number; totalToday: number }
      >(functions, 'refreshUsage');

      await refreshUsageFn({ orgId: organization.id });
      
      // The Firestore listener will pick up the update automatically
    } catch (err) {
      console.error('Usage refresh error:', err);
      setError(err as Error);
    } finally {
      setRefreshing(false);
    }
  }, [canRefresh, organization?.id, refreshing]);

  return {
    usage,
    loading,
    error,
    refresh,
    canRefresh,
    refreshing,
  };
}

// =============================================================================
// Additional Hook: Previous Month Usage
// =============================================================================

/**
 * Hook to get previous month's usage for comparison.
 */
export function usePreviousMonthUsage(): { 
  previousTotal: number | null; 
  loading: boolean; 
} {
  const { organization } = useAuth();
  const [previousTotal, setPreviousTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const previousPeriod = getPreviousPeriodId();

  useEffect(() => {
    if (!organization?.id) {
      setLoading(false);
      return;
    }

    const usageRef = doc(db, 'organizations', organization.id, 'usage', previousPeriod);

    const unsubscribe = onSnapshot(
      usageRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setPreviousTotal(snapshot.data().totalRequests || 0);
        } else {
          setPreviousTotal(0);
        }
        setLoading(false);
      },
      () => {
        setPreviousTotal(0);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [organization?.id, previousPeriod]);

  return { previousTotal, loading };
}
